
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Table,
    Spinner,
    Button,
    ButtonGroup
} from 'reactstrap';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import { FaArrowRight, FaDownload, FaPrint } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import './ViewExam.css';
import {
    createLandscapePdf,
    standardAutoTableOptions,
    drawCenteredFooter,
} from '../../utils/pdfLayout';

const baseURL = apiURL1;

function ComputeResultTab({ classId, type, typeId, year, examDetails, uniClassName, examId }) {
    console.log('ComputeResultTab received examId:', examId);
    const [activeTab, setActiveTab] = useState('byClass');
    const navigate = useNavigate();

    return (
        <Container>
            <Row>
                <Col>
                    <div className="d-flex justify-content-center align-items-center mb-5" style={{ gap: '8px' }}>
                        <Button
                            size="sm"
                            color={activeTab === "byClass" ? "info" : "secondary"}
                            onClick={() => setActiveTab("byClass")}
                        >
                            By Class
                        </Button>
                        <Button
                            size="sm"
                            color={activeTab === "bySubject" ? "info" : "secondary"}
                            onClick={() => setActiveTab("bySubject")}
                        >
                            By Subject
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    {activeTab === 'byClass' && (
                        <ByClassResults classId={classId} type={type} typeId={typeId} year={year} uniClassName={uniClassName} examId={examId} />
                    )}
                    {activeTab === 'bySubject' && (
                        <BySubjectList examDetails={examDetails} navigate={navigate} classId={classId} type={type} typeId={typeId} year={year} uniClassName={uniClassName} />
                    )}
                </Col>
            </Row>
        </Container>
    );
}

function ByClassResults({ classId, type, typeId, year, uniClassName, examId }) {
    const navigate = useNavigate();
    const { id } = useParams();
    // Fallback to URL param if examId prop is missing (safe since this component is mainly used in SubjectResult route)
    const effectiveExamId = examId || id;

    console.log('ByClassResults:', { propExamId: examId, paramId: id, effectiveExamId });

    const [results, setResults] = useState(null);
    const [overallStatus, setOverallStatus] = useState('');
    const [schoolInfo, setSchoolInfo] = useState({});
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchComputeResults();
        fetchSchoolInfo();
    }, [classId, typeId, year, effectiveExamId]);

    const fetchSchoolInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            const data = response.data;
            if (data && data.length > 0) {
                setSchoolInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching school info:', error);
        }
    };

    const fetchComputeResults = async () => {
        setLoading(true);
        try {
            // Try new class-level route first
            let newUrl = `${baseURL}/superadmin/results/class/${classId}/type/${type}/year/${year}`;
            if (effectiveExamId) {
                newUrl += `?examId=${effectiveExamId}`;
            }
            console.log('Fetching compute results from:', newUrl);
            const resNew = await axios.get(newUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            const payload = resNew.data || {};
            const invalid = payload && (payload.success === false || payload.error);
            const hasStudents = Array.isArray(payload.students) || Array.isArray(payload.data);
            if (invalid || !hasStudents) {
                throw new Error(payload?.message || 'Invalid class results response');
            }
            const students = payload.students || payload.data || [];
            const topThree = payload.topThree || students
                .slice()
                .sort((a, b) => (b.marksObtained ?? 0) - (a.marksObtained ?? 0))
                .slice(0, 3)
                .map((s, idx) => ({
                    rank: s.rank ?? (idx + 1),
                    studentId: s.studentId || s._id || s.id,
                    fullName: s.fullName,
                    registrationNumber: s.registrationNumber,
                    obtainedMarks: s.marksObtained,
                    totalMarks: s.totalMarks,
                    grade: s.grade,
                }));

            setResults({
                data: students,
                topThree,
                overallStatus: payload.overallStatus || 'pending',
            });
            setOverallStatus(payload.overallStatus || 'pending');
        } catch (errorNew) {
            // Fallback to existing route
            try {
                let oldUrl = `${baseURL}/superadmin/results/class/${classId}/type/${typeId}/year/${year}`;
                if (effectiveExamId) {
                    oldUrl += `?examId=${effectiveExamId}`;
                }
                const response = await axios.get(oldUrl, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                });
                setResults(response.data);
                setOverallStatus(response.data.overallStatus || 'pending');
            } catch (errorOld) {
                console.error('Error fetching computed results:', errorOld);
                toast.error('Error fetching computed results');
            }
        } finally {
            setLoading(false);
        }
    };

    const generatePdfReport = async () => {
        try {
            const schoolResponse = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const schoolData = schoolResponse.data[0] || {};

            const pdf = createLandscapePdf(jsPDF);
            const pageWidth = pdf.internal.pageSize.getWidth();

            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text(schoolData.schoolName || 'Ummu Dhardha Ladies Islamic College', pageWidth / 2, 14, { align: 'center' });
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'normal');
            pdf.text(schoolData.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.', pageWidth / 2, 20, { align: 'center' });
            pdf.text(
                `Tel: ${schoolData.schoolPhoneNumber || '+94 67 205 3211'} | Email: ${schoolData.schoolEmail || 'info@ummudhardha.lk'}`,
                pageWidth / 2,
                25,
                { align: 'center' }
            );

            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Class Results - ${type} ${year}`, pageWidth / 2, 34, { align: 'center' });
            pdf.setFontSize(10);
            pdf.text(`Class: ${uniClassName || ''}`, pageWidth / 2, 40, { align: 'center' });

            let y = 46;
            const rankColors = [
                [46, 204, 113],
                [52, 152, 219],
                [155, 89, 182],
            ];

            if (results.topThree && results.topThree.length > 0) {
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Top Three Performers', 14, y);
                y += 6;

                results.topThree.forEach((student, index) => {
                    const [r, g, b] = rankColors[index] || [100, 100, 100];
                    pdf.setFillColor(r, g, b);
                    pdf.roundedRect(14, y, pageWidth - 28, 12, 2, 2, 'F');
                    pdf.setTextColor(255, 255, 255);
                    pdf.setFontSize(9);
                    pdf.setFont('helvetica', 'bold');
                    pdf.text(
                        `Rank ${student.rank}: ${student.fullName}  |  Reg: ${student.registrationNumber}  |  Marks: ${student.obtainedMarks ?? '-'}/${student.totalMarks ?? '-'}  |  Grade: ${student.grade || '-'}`,
                        18,
                        y + 8
                    );
                    y += 15;
                    pdf.setTextColor(0, 0, 0);
                });
                y += 4;
            }

            if (results.data && results.data.length > 0) {
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'bold');
                pdf.text('Detailed Results', 14, y);
                y += 4;

                const headers = [[
                    'Rank',
                    'Reg No.',
                    'Student Name',
                    'Mid Marks',
                    'Mid Components',
                    'Final Marks',
                    'Obtained',
                    'Total',
                    'Grade',
                ]];

                const tableData = results.data.map((student) => {
                    const subjects = Array.isArray(student.subjects) ? student.subjects : [];
                    const finObt = subjects.reduce((sum, sub) => sum + (Number(sub.subjectObtainMarksFinal) || 0), 0);
                    const finTot = subjects.reduce((sum, sub) => sum + (Number(sub.subjectTotalMarksFinal) || 0), 0);
                    const midSubjectPair = `${student.midSubjectObtainedMarks ?? '-'}${student.midSubjectTotalMarks != null ? '/' + student.midSubjectTotalMarks : '/-'}`;
                    const midComponentsPair = `${student.midComponentsObtainedTotal ?? '-'}${student.midComponentsTotalMarks != null ? '/' + student.midComponentsTotalMarks : '/-'}`;
                    const finalPair = finTot > 0 ? `${finObt}/${finTot}` : '-/-';
                    const obtained = student.obtainedMarks ?? '-';
                    const total = student.totalMarks ?? '-';
                    const grade = student.grade && student.grade !== '-' ? student.grade : (student.grade || '-');

                    return [
                        student.rank,
                        student.registrationNumber || '-',
                        student.fullName || '-',
                        midSubjectPair,
                        midComponentsPair,
                        finalPair,
                        obtained,
                        total,
                        grade,
                    ];
                });

                pdf.autoTable(standardAutoTableOptions({
                    startY: y,
                    head: headers,
                    body: tableData,
                    styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak', valign: 'middle', halign: 'center' },
                    columnStyles: {
                        0: { cellWidth: 14 },
                        1: { cellWidth: 28 },
                        2: { cellWidth: 55, halign: 'left' },
                        3: { cellWidth: 28 },
                        4: { cellWidth: 32 },
                        5: { cellWidth: 28 },
                        6: { cellWidth: 24 },
                        7: { cellWidth: 24 },
                        8: { cellWidth: 18 },
                    },
                }));
            }

            drawCenteredFooter(pdf, schoolData.schoolName);
            pdf.save(`results-${uniClassName}-${type}-${year}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Error generating PDF report');
        }
    };

    const handlePrint = async () => {
        try {
            const schoolResponse = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const schoolData = schoolResponse.data[0] || {};

            const tableStyle = `
        border-collapse: collapse;
        width: 100%;
        margin-top: 20px;
      `;

            const thStyle = `
        background-color: #2980b9;
        color: white;
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      `;

            const tdStyle = `
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      `;

            const printContent = `
        <html>
          <head>
            <title>Class Results - ${uniClassName}</title>
            <style>
              @page { margin: 20px; }
              body { font-family: Arial; margin: 20px; }
              .header { text-align: center; margin-bottom: 20px; }
              .logo { width: 100px; height: 100px; }
              .school-name { font-size: 24px; font-weight: bold; margin: 10px 0; }
              .report-title { font-size: 20px; margin: 15px 0; }
              table { width: 100%; border-collapse: collapse; margin-top: 15px; }
              th { background-color: #2980b9; color: white; padding: 8px; text-align: left; }
              td { padding: 8px; border-bottom: 1px solid #ddd; }
              .top-rank { background-color: #2ecc71; color: white; margin: 10px 0; padding: 10px; border-radius: 5px; }
              .footer { margin-top: 20px; font-size: 12px; text-align: center; }
            </style>
          </head>
          <body>
            <div class="header">
              <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" 
                   class="logo" alt="School Logo">
              <div class="school-name">${schoolData.schoolName || 'Ummu Dhardha Ladies Islamic College'}</div>
              <div class="school-info">
                ${schoolData.schoolAddress || 'Meera Nagar, Beach Road, Akkaraipattu. EP. 32400. Sri Lanka.'}<br>
                Tel: ${schoolData.schoolPhoneNumber || '+94 67 205 3211'} | 
                Email: ${schoolData.schoolEmail || 'info@ummudhardha.lk'}
              </div>
            </div>
            
            <div class="report-title">Class Results - ${type} ${year}</div>
            <div class="class-name">Class: ${uniClassName}</div>

            ${results.topThree && results.topThree.length > 0 ? `
              <h3>Top Three Performers</h3>
              ${results.topThree.map((student, index) => `
                <div class="top-rank" style="background-color: ${['#2ecc71', '#3498db', '#9b59b6'][index]}">
                  <h4>Rank ${student.rank}: ${student.fullName}</h4>
                  <p>Registration: ${student.registrationNumber}</p>
                  <p>Marks: ${student.obtainedMarks}/${student.totalMarks} | Grade: ${student.grade}</p>
                </div>
              `).join('')}
            ` : ''}

            ${results.data && results.data.length > 0 ? `
              <h3>All Student Results</h3>
                <table style="${tableStyle}">
              <thead>
                <tr>
                    <th style="${thStyle}">Rank</th>
                    <th style="${thStyle}">Registration Number</th>
                    <th style="${thStyle}">Name</th>
                    <th style="${thStyle}">Subject Mid Marks</th>
                    <th style="${thStyle}">Mid Components Marks</th>
                    <th style="${thStyle}">Subject Final Marks</th>
                    <th style="${thStyle}">Obtained Marks</th>
                    <th style="${thStyle}">Total Marks</th>
                    <th style="${thStyle}">Grade</th>
                  </tr>
                  </thead>
                  <tbody>
                    ${results.data.map(student => {
                const subjects = Array.isArray(student.subjects) ? student.subjects : [];
                return `
                      <tr>
                        <td style="${tdStyle}">${student.rank ?? '-'}</td>
                        <td style="${tdStyle}">${student.registrationNumber ?? '-'}</td>
                        <td style="${tdStyle}">${student.fullName ?? '-'}</td>
                        <td style="${tdStyle}">${(() => {
                        const pair = `${(student.midSubjectObtainedMarks ?? '-')}${(student.midSubjectTotalMarks ? '/' + student.midSubjectTotalMarks : '/-')}`;
                        return pair;
                    })()}</td>
                        <td style="${tdStyle}">${(() => {
                        const pair = `${(student.midComponentsObtainedTotal ?? '-')}${(student.midComponentsTotalMarks ? '/' + student.midComponentsTotalMarks : '/-')}`;
                        return pair;
                    })()}</td>
                        <td style="${tdStyle}">${(() => {
                        const pair = (student.finalSubjectObtainedMarks !== undefined && student.finalSubjectTotalMarks !== undefined)
                            ? `${student.finalSubjectObtainedMarks}/${student.finalSubjectTotalMarks}`
                            : (() => {
                                const finalObt = subjects.reduce((sum, sub) => sum + (Number(sub.finalOutsideObtained) || 0), 0);
                                const finalTot = subjects.reduce((sum, sub) => sum + (Number(sub.finalSubjectTotalMarks) || 0), 0);
                                return `${finalObt || '-'}${finalTot ? '/' + finalTot : '/-'}`;
                            })();
                        return pair;
                    })()}</td>
                        <td style="${tdStyle}">${student.obtainedMarks ?? '-'}</td>
                        <td style="${tdStyle}">${student.totalMarks ?? '-'}</td>
                        <td style="${tdStyle}">${student.grade ?? '-'}</td>
                      </tr>
                        `;
            }).join('')}
                  </tbody>
                </table>
            ` : ''}

            <div class="footer">
              Generated on: ${new Date().toLocaleDateString()} | ${schoolData.schoolName || 'Ummu Dhardha Ladies Islamic College'}
            </div>
          </body>
        </html>
      `;

            const printWindow = window.open('', '_blank');
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.print();
        } catch (error) {
            console.error('Error generating print content:', error);
            toast.error('Error generating print view');
        }
    };

    const handleStatusUpdate = async () => {
        setLoading(true);
        const newStatus = overallStatus === 'published' ? 'pending' : 'published';
        try {
            await axios.put(`${baseURL}/superadmin/results/live/${effectiveExamId}`, { status: newStatus }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            toast.success(`Results status changed to ${newStatus === 'published' ? 'Published' : 'Draft'}!`);
            fetchComputeResults(); // Refresh results
        } catch (error) {
            console.error('Error updating results status:', error);
            toast.error('Error updating results status');
        } finally {
            setLoading(false);
        }
    };

    const handleStudentClick = (studentId) => {
        navigate(`/admin/view-student-result/${studentId}/${typeId}/${year}`, {
            state: {
                className: uniClassName,
                typeName: type,
                year: year,
            }
        });
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <Spinner color="primary" />
                <p>Loading computed results...</p>
            </div>
        );
    }

    if (!results) {
        return <div className="text-center p-5">No results computed for this class yet.</div>;
    }

    const rankColors = ['#2ecc71', '#3498db', '#9b59b6'];

    return (
        <div className="bg-white rounded shadow-sm p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-0 text-primary">Class Performance Overview {uniClassName ? `- ${uniClassName}` : ''}</h3>
                    <p className="mb-0 mt-1">
                        Current Status: <span className={overallStatus === 'published' ? 'text-success font-weight-bold' : 'text-warning font-weight-bold'}>
                            {overallStatus === 'published' ? 'PUBLISHED' : 'DRAFT'}
                        </span>
                    </p>
                </div>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    <Button
                        size="sm"
                        color={overallStatus === 'published' ? 'warning' : 'success'}
                        onClick={handleStatusUpdate}
                        disabled={loading}
                        className="d-inline-flex align-items-center"
                    >
                        {overallStatus === 'published' ? 'Make Draft' : 'Publish'}
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        outline
                        onClick={generatePdfReport}
                        className="d-inline-flex align-items-center"
                    >
                        <FaDownload className="mr-2" />
                        Download Report
                    </Button>
                </div>
            </div>

            <div className="print-only">
                <h4>Class Results - {type} {year}</h4>
                {results.topThree && results.topThree.length > 0 && (
                    <>
                        <h5>Top Three Ranks</h5>
                        {results.topThree.map((student, index) => (
                            <div key={student.studentId}>
                                <p>Rank {student.rank}: {student.fullName} (Reg: {student.registrationNumber}, Obtained: {student.obtainedMarks}, Total: {student.totalMarks}, Grade: {student.grade})</p>
                            </div>
                        ))}
                    </>
                )}
            </div>

            {results.topThree && results.topThree.length > 0 ? (
                <Row className="">
                    {results.topThree.map((student, index) => {
                        return (
                            <Col md="4" key={student.studentId} className="mb-3">
                                <Card style={{
                                    backgroundColor: rankColors[index],
                                    color: 'white',
                                    textAlign: 'center',
                                    borderRadius: '15px',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                }}>
                                    <CardBody>
                                        <h5>Rank: {student.rank}</h5>
                                        <p>Name: {student.fullName}</p>
                                        <p>Registration Number: {student.registrationNumber}</p>
                                        <p>Obtained Marks: {student.obtainedMarks ?? '-'}</p>
                                        <p>Total Marks: {student.totalMarks ?? '-'}</p>
                                        <p>Grade: {student.grade || '-'}</p>
                                    </CardBody>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            ) : (
                <p>No top three ranks available.</p>
            )}

            {results.data && results.data.length > 0 ? (
                <>
                    <h4>All Student Results</h4>
                    <div className="table-responsive">
                        <Table className="table">
                            <thead>
                                <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                    <th>Rank</th>
                                    <th>Registration Number</th>
                                    <th>Name</th>
                                    <th>Subject Mid Marks</th>
                                    <th>Mid Components Marks</th>
                                    <th>Subject Final Marks</th>
                                    <th>Obtained Marks</th>
                                    <th>Total Marks</th>
                                    <th>Grade</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.data.map((student) => (
                                    <tr key={student.studentId}>
                                        <td>{student.rank}</td>
                                        <td>{student.registrationNumber}</td>
                                        <td>{student.fullName}</td>

                                        <td>{(() => {
                                            const pair = `${(student.midSubjectObtainedMarks ?? '-')}${(student.midSubjectTotalMarks ? '/' + student.midSubjectTotalMarks : '/-')}`;
                                            return pair;
                                        })()}</td>
                                        <td>{(() => {
                                            const pair = `${(student.midComponentsObtainedTotal ?? '-')}${(student.midComponentsTotalMarks ? '/' + student.midComponentsTotalMarks : '/-')}`;
                                            return pair;
                                        })()}</td>
                                        <td>{(() => {
                                            const subjects = Array.isArray(student.subjects) ? student.subjects : [];
                                            const finObt = subjects.reduce((sum, sub) => sum + (Number(sub.subjectObtainMarksFinal) || 0), 0);
                                            const finTot = subjects.reduce((sum, sub) => sum + (Number(sub.subjectTotalMarksFinal) || 0), 0);
                                            return finTot > 0 ? `${finObt}/${finTot}` : `-/-`;
                                        })()}</td>
                                        <td>{student.obtainedMarks ?? '-'}</td>
                                        <td>{student.totalMarks ?? '-'}</td>
                                        <td>{student.grade || '-'}</td>
                                        <td className="text-center align-middle">
                                            <Button
                                                color="link"
                                                className="p-0 d-inline-flex align-items-center justify-content-center"
                                                style={{ width: 32, height: 32 }}
                                                onClick={() => handleStudentClick(student.studentId)}
                                                title="View student"
                                            >
                                                <FaArrowRight />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </>
            ) : (
                <p>No student results available.</p>
            )}
        </div>
    );
}


function BySubjectList({ examDetails, navigate, classId, type, typeId, year, uniClassName }) {
    // console.log('Exam details is ', examDetails)
    const handleSubjectClick = (subjectId, subjectName) => {
        navigate(`/admin/view-subject-result/${classId}/${subjectId}/${typeId}/${year}`, {
            state: {
                className: uniClassName ? uniClassName : 'N/A',
                subjectName: subjectName,
                typeName: type,
                typeId: typeId,
                year: year
            }
        });
    };

    return (
        <Container>
            {examDetails && Array.isArray(examDetails) && examDetails.length > 0 ? (
                <div className="table-responsive">
                    <Table className="table">
                        <thead>
                            <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                <th>Subject Code</th>
                                <th>Subject</th>
                                <th>Staff</th>
                                <th>Date</th>
                                <th>Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {examDetails.map((detail, index) => (
                                <tr key={detail._id} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : 'white' }}>
                                    <td>{detail.subject.subjectCode}</td>
                                    <td>{detail.subject.name}</td>
                                    <td>{detail.teacher ? detail.teacher.name : 'N/A'}</td>
                                    <td>{dayjs(detail.examDate).format('DD-MM-YYYY')}</td>
                                    <td style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSubjectClick(detail.subject._id, detail.subject.name)}>
                                        <FaArrowRight />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <p>No subjects available.</p>
            )}
        </Container>
    );
}

export default ComputeResultTab;
